import {
  DispositionType,
  PreparationOrder,
  Shipment,
  ShippingRecord,
  ShippingRecordDispute,
  ShippingRecordDisputeType,
} from "$types";
import { removeDuplicates } from "$utils/utils";
import { PreparationOrderErrors } from "./PreparationOrder";

export const SHIPPING_RECORD_DISPUTE_TYPE_LABELS: {
  [key in ShippingRecordDisputeType]: string;
} = {
  [ShippingRecordDisputeType.SHIPPING_RECORD]: "Dossier",
  [ShippingRecordDisputeType.PREPARATION_ORDER]: "Ordre de préparation",
  [ShippingRecordDisputeType.DELIVERY]: "Livraison",
};

export const getShippingRecordDisputeTypeLabel = (
  shippingRecordDisputeType: ShippingRecordDisputeType
): string => SHIPPING_RECORD_DISPUTE_TYPE_LABELS[shippingRecordDisputeType];

export const getShippingRecordDisputeTypeOptions = () =>
  Object.entries(SHIPPING_RECORD_DISPUTE_TYPE_LABELS).map(([key, value]) => ({
    value: key,
    label: value,
  }));

export const canAddDispute = (shippingRecord: ShippingRecord): boolean => {
  if (!shippingRecord) return false;
  const { shipments, shippingRecordDisputes } = shippingRecord;
  const safeShipments: Shipment[] = Array.isArray(shipments) ? shipments : [];
  const maxShippingRecordDisputes = 1;
  const maxOpDisputes = safeShipments.length;
  const maxDeliveryDisputes = removeDuplicates(
    safeShipments
      .map((shipment: Shipment) => shipment?.toAddress?.entityId)
      .filter(Boolean)
  ).length;
  const openDisputes = (shippingRecordDisputes ?? []).reduce(
    (acc, curr) => {
      if (curr.closedAt) {
        return acc;
      }
      return {
        ...acc,
        [curr.type]: acc[curr.type] + 1,
      };
    },
    {
      [ShippingRecordDisputeType.PREPARATION_ORDER]: 0,
      [ShippingRecordDisputeType.DELIVERY]: 0,
      [ShippingRecordDisputeType.SHIPPING_RECORD]: 0,
    }
  );

  return (
    openDisputes[ShippingRecordDisputeType.PREPARATION_ORDER] < maxOpDisputes ||
    openDisputes[ShippingRecordDisputeType.DELIVERY] < maxDeliveryDisputes ||
    openDisputes[ShippingRecordDisputeType.SHIPPING_RECORD] <
      maxShippingRecordDisputes
  );
};

export const getShippingRecordDisputeTypeInfo = (
  dispute: ShippingRecordDispute,
  shippingRecord: ShippingRecord
): { label: string; location?: string } => {
  switch (dispute.type) {
    case ShippingRecordDisputeType.SHIPPING_RECORD:
      return { label: shippingRecord.code };
    case ShippingRecordDisputeType.PREPARATION_ORDER:
      return {
        label:
          shippingRecord?.shipments
            .map((shipment) => shipment.op)
            .find((op: PreparationOrder) => op?._id === dispute.linkedEntityId)
            ?.code || PreparationOrderErrors.OP_NOT_FOUND,
      };
    case ShippingRecordDisputeType.DELIVERY: {
      const op = shippingRecord?.shipments
        .map((shipment) => shipment.op)
        .find(
          (op: PreparationOrder) =>
            op?.chantier?._id === dispute.linkedEntityId ||
            op?.shipEntrepotName === dispute.linkedEntityId
        );
      if (!op) return { label: "" };
      return {
        label:
          op.shipType === DispositionType.ON_DOCKYARD
            ? `${op?.chantier?.code || ""} ${op?.chantier?.name || ""}`
            : `${op?.shipEntrepotName || ""}`,
        location: `${op?.shipPostalCode || ""} ${op?.shipCity || ""}`,
      };
    }
    default:
      return { label: "" };
  }
};
